import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Tabs,
  Tab
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  getReadOnlyCapabilitiesTree,
  getViewPreferences,
  setViewPreferences,
  getDefaultReadOnlyConfig
} from '../viewSettings';
import { useData } from '../DataProvider';

// Capabilities that only mean something for a Windows run: the active-window border needs
// WinHookEvent rows, which the macOS collector never emits.
const WINDOWS_ONLY_CAPABILITIES = new Set(['overlayActiveWindow']);

// Compact checkbox style: tighter padding on the icon and smaller label text
const compactCheckboxSx = { p: 0.25, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } };
const compactLabelSx = { ml: 0, mr: 1.5, '& .MuiFormControlLabel-label': { fontSize: '0.82rem' } };

function renderGroups(draft, handleToggle, groups) {
  const renderGroup = (group) => {
    const sectionId = group.sectionId;
    const sectionOn = sectionId ? !!draft[sectionId] : true;
    const [sectionCap, ...childCaps] = group.capabilities;

    return (
      <Box
        key={group.id}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          py: 0.5,
          '&:last-of-type': { borderBottom: 0 }
        }}
      >
        {sectionId && sectionCap ? (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  sx={compactCheckboxSx}
                  checked={draft[sectionCap.id] !== undefined ? !!draft[sectionCap.id] : false}
                  onChange={(e) => handleToggle(sectionCap.id, e.target.checked)}
                />
              }
              label={<Typography variant="body2" fontWeight={600}>{group.label}</Typography>}
              labelPlacement="end"
              sx={{ ...compactLabelSx, ml: 0 }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', pl: 2.5, gap: 0 }}>
              {childCaps.map(({ id, label }) => (
                <FormControlLabel
                  key={id}
                  control={
                    <Checkbox
                      size="small"
                      sx={compactCheckboxSx}
                      checked={draft[id] !== undefined ? !!draft[id] : false}
                      onChange={(e) => handleToggle(id, e.target.checked)}
                      disabled={!sectionOn}
                    />
                  }
                  label={label}
                  labelPlacement="end"
                  sx={{ ...compactLabelSx, opacity: sectionOn ? 1 : 0.5 }}
                />
              ))}
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {group.label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', pl: 2.5, gap: 0 }}>
              {group.capabilities.map(({ id, label }) => (
                <FormControlLabel
                  key={id}
                  control={
                    <Checkbox
                      size="small"
                      sx={compactCheckboxSx}
                      checked={draft[id] !== undefined ? !!draft[id] : false}
                      onChange={(e) => handleToggle(id, e.target.checked)}
                    />
                  }
                  label={label}
                  labelPlacement="end"
                  sx={compactLabelSx}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  };
  return (groups || []).map(renderGroup);
}

/**
 * View settings dialog.
 *
 * Tab 0 – View preferences: auto-saved to localStorage on every toggle (instant).
 * Tab 1 – Read-only defaults: edited locally, then explicitly saved to Firestore
 *          via the "Save to cloud" button. Unsaved changes are detected and the
 *          user is prompted before closing.
 */
export default function ViewSettingsDialog({ open, onClose, onSave, runId = null, appName = null, initialReadOnlyConfig = null, onSaveReadOnlyDefaults }) {
  const [tree, setTree] = useState({ groups: [] });
  const [viewPrefs, setViewPrefsState] = useState({});
  const [draftReadOnlyDefaults, setDraftReadOnlyDefaults] = useState({});
  // Snapshot of the read-only config as it was when the dialog opened (used for dirty detection)
  const [baselineReadOnly, setBaselineReadOnly] = useState({});
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);

  useEffect(() => {
    if (open) {
      setTree(getReadOnlyCapabilitiesTree());
      setViewPrefsState(getViewPreferences());
      const initial = initialReadOnlyConfig && typeof initialReadOnlyConfig === 'object' ? { ...initialReadOnlyConfig } : getDefaultReadOnlyConfig();
      setDraftReadOnlyDefaults(initial);
      setBaselineReadOnly(initial);
      setTab(0);
      setSaving(false);
      setSaved(false);
      setConfirmingClose(false);
    }
  }, [open, initialReadOnlyConfig]);

  // Dirty detection: compare draft read-only defaults against baseline
  const readOnlyDirty = useMemo(() => {
    if (!runId) return false;
    const keys = new Set([...Object.keys(baselineReadOnly), ...Object.keys(draftReadOnlyDefaults)]);
    for (const key of keys) {
      if (!!baselineReadOnly[key] !== !!draftReadOnlyDefaults[key]) return true;
    }
    return false;
  }, [baselineReadOnly, draftReadOnlyDefaults, runId]);

  // View preferences: auto-save to localStorage on every toggle
  const handleToggleViewPrefs = (id, enabled) => {
    setViewPrefsState((prev) => {
      const next = { ...prev, [id]: !!enabled };
      setViewPreferences(next);
      onSave?.();
      return next;
    });
  };

  // Read-only defaults: local draft only (saved explicitly)
  const handleToggleReadOnlyDefaults = (id, enabled) => {
    setDraftReadOnlyDefaults((prev) => ({ ...prev, [id]: !!enabled }));
    setSaved(false);
  };

  const handleSaveReadOnlyDefaults = async () => {
    if (!runId || typeof onSaveReadOnlyDefaults !== 'function') return;
    setSaving(true);
    try {
      await onSaveReadOnlyDefaults(runId, draftReadOnlyDefaults);
      // Update baseline so dirty flag clears after save
      setBaselineReadOnly({ ...draftReadOnlyDefaults });
      setSaved(true);
    } finally {
      setSaving(false);
    }
    onSave?.();
  };

  // Close handler: if dirty, show inline confirmation instead of closing
  const handleClose = () => {
    if (readOnlyDirty) {
      setConfirmingClose(true);
      setTab(1); // switch to read-only tab so user sees the context
      return;
    }
    onClose?.();
  };

  const handleDiscardAndClose = () => {
    setConfirmingClose(false);
    onClose?.();
  };

  const { platform } = useData();
  const groups = useMemo(() => {
    const all = tree.groups || [];
    if (platform !== 'macos') return all;
    return all
      .map((g) => ({ ...g, capabilities: g.capabilities.filter((c) => !WINDOWS_ONLY_CAPABILITIES.has(c.id)) }))
      .filter((g) => g.capabilities.length > 0);
  }, [tree, platform]);

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" sx={{ '& .MuiDialog-paper': { width: 600, maxWidth: '100%' } }}>
      <DialogTitle sx={{ pb: 0 }}>View settings</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
          <Tab label="Local settings" id="view-settings-tab-0" aria-controls="view-settings-panel-0" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                App read-only
                {readOnlyDirty && (
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }} />
                )}
              </Box>
            }
            id="view-settings-tab-1"
            aria-controls="view-settings-panel-1"
          />
        </Tabs>

        {/* View preferences tab: auto-saved to localStorage on every toggle */}
        <Box role="tabpanel" hidden={tab !== 0} id="view-settings-panel-0" aria-labelledby="view-settings-tab-0">
          {tab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <Box sx={{ bgcolor: 'action.hover', borderLeft: '3px solid', borderColor: 'info.main', borderRadius: 1, px: 1.5, py: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  These settings are saved in your browser and apply only to you. Changes take effect immediately.
                </Typography>
              </Box>
              {renderGroups(viewPrefs, handleToggleViewPrefs, groups)}
            </Box>
          )}
        </Box>

        {/* Read-only defaults tab: draft edited locally, saved to Firestore explicitly */}
        <Box role="tabpanel" hidden={tab !== 1} id="view-settings-panel-1" aria-labelledby="view-settings-tab-1">
          {tab === 1 && (
            <>
              {runId ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <Box sx={{ bgcolor: 'action.hover', borderLeft: '3px solid', borderColor: 'info.main', borderRadius: 1, px: 1.5, py: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {appName
                        ? <>These settings apply to <strong>{appName}</strong> in read-only mode. They are saved to the cloud and shared with everyone who opens this run.</>
                        : <>These settings apply in read-only mode. They are saved to the cloud and shared with everyone who opens this run.</>
                      }
                    </Typography>
                  </Box>
                  {renderGroups(draftReadOnlyDefaults, handleToggleReadOnlyDefaults, groups)}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ display: 'block', py: 1 }}>
                  Select a run to configure read-only defaults for this app.
                </Typography>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        {confirmingClose ? (
          // Inline confirmation: replaces normal footer when user tries to close with unsaved changes
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 1 }}>
            <Typography variant="body2" color="warning.main">
              Unsaved changes{appName ? ` for ${appName}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<CloudUploadIcon />}
                disabled={saving}
                onClick={async () => {
                  await handleSaveReadOnlyDefaults();
                  setConfirmingClose(false);
                  onClose?.();
                }}
              >
                {saving ? 'Saving...' : 'Save & close'}
              </Button>
              <Button variant="outlined" size="small" color="inherit" onClick={handleDiscardAndClose}>
                Discard
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {tab === 1 && runId && (
              <>
                {readOnlyDirty && (
                  <Typography variant="caption" color="warning.main" sx={{ mr: 'auto' }}>
                    Unsaved changes
                  </Typography>
                )}
                {saved && !readOnlyDirty && (
                  <Typography variant="caption" color="success.main" sx={{ mr: 'auto' }}>
                    Saved
                  </Typography>
                )}
                <Button
                  variant="contained"
                  onClick={handleSaveReadOnlyDefaults}
                  disabled={saving || !readOnlyDirty}
                  startIcon={<CloudUploadIcon />}
                >
                  {saving ? 'Saving...' : 'Save to cloud'}
                </Button>
              </>
            )}
            <Button onClick={handleClose}>Close</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
